import { z } from 'zod';
import { haversineMeters } from './geo.js';
import { dateRangeQuerySchema } from './pagination.js';
/**
 * Shift tracking (D87-D92). Location is collected only between "بدء الشيفت" and
 * "إنهاء الشيفت", only after the agent agreed to the policy below, and keeps
 * running in the background. Raw points are kept for a configurable window;
 * the daily summary is permanent.
 */
// ---------------------------------------------------------------------------
// Consent
// ---------------------------------------------------------------------------
/** Bump when the text below changes: every agent is asked again. */
export const TRACKING_POLICY_VERSION = '2026-10-12';
/** What the consent screen says. Stored with the version the agent agreed to. */
export const TRACKING_POLICY_TEXT = {
    ar: {
        title: 'تتبع الموقع أثناء الشيفت',
        what: 'بنسجّل موقعك (خط العرض وخط الطول ودقة الموقع)، ووقت التسجيل، ونسبة البطارية، وهل الموقع من تطبيق موقع وهمي، والزيارة اللي شغال عليها لو فيه.',
        when: 'التسجيل بيبدأ لما تدوس "بدء الشيفت" وبيقف لما تدوس "إنهاء الشيفت". بيكمّل والتطبيق في الخلفية. قفل التطبيق مش بينهي الشيفت. لو نسيت، الشيفت بيقفل لوحده في الميعاد اللي الإدارة محدداه. مفيش أي تسجيل برّه الشيفت.',
        who: 'بيشوفه المدير المباشر والإدارة اللي عندهم صلاحية "عرض موقع المندوب" بس، وكل مرة حد بيفتح موقعك بتتسجل. إنت شايف مسارك على طول.',
        keep: 'النقاط التفصيلية بتتمسح بعد {{days}} يوم. ملخص اليوم (عدد الساعات والزيارات والكيلومترات) بيفضل محفوظ.',
        refuse: 'لو مش موافق، التطبيق بيشتغل عادي في الزيارات، بس مفيش تتبع للشيفت.',
        accept: 'أوافق',
        decline: 'مش دلوقتي',
    },
    en: {
        title: 'Location tracking during your shift',
        what: 'We record your location (latitude, longitude and accuracy), the time, your battery level, whether the location comes from a mock-location app, and the visit you are working on, if any.',
        when: 'Recording starts when you tap "Start shift" and stops when you tap "End shift". It continues while the app is in the background. Closing the app does not end the shift. If you forget, the shift closes by itself at the time set by management. Nothing is recorded outside a shift.',
        who: 'Only your direct manager and staff with the "View agent location" permission can see it, and every time someone opens your location it is logged. You can always see your own track.',
        keep: 'Detailed points are deleted after {{days}} days. The daily summary (hours, visits, kilometres) is kept.',
        refuse: 'If you do not agree, the app works normally for visits, without shift tracking.',
        accept: 'I agree',
        decline: 'Not now',
    },
};
export const consentRequestSchema = z.object({ policyVersion: z.string().min(1).max(40), accepted: z.boolean() });
export const consentStatusSchema = z.object({
    currentPolicyVersion: z.string(),
    /** Consent to the current version. An older version does not count. */
    consented: z.boolean(),
    acceptedVersion: z.string().nullable(),
    acceptedAt: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Settings (all values in config; SUPER_ADMIN edits them in The Engine)
// ---------------------------------------------------------------------------
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time is HH:mm (24 h)');
export const trackingSettingsSchema = z.object({
    /** The OS is asked for a fix this often; the phone keeps it when it moved `sampleDistanceMeters` (D88). */
    sampleMinIntervalSeconds: z.number().int().min(15).max(600),
    /** ...or when this long passed since the last kept point, whichever comes first. */
    sampleMaxIntervalSeconds: z.number().int().min(30).max(1800),
    sampleDistanceMeters: z.number().int().min(10).max(1000),
    /** BALANCED (cell/Wi-Fi assisted, ~100 m) or HIGH (GPS). Balanced saves battery. */
    accuracy: z.enum(['BALANCED', 'HIGH']),
    uploadIntervalSeconds: z.number().int().min(60).max(3600),
    uploadBatchSize: z.number().int().min(5).max(200),
    /** A reminder (local notification) if the shift is still open at this time. */
    shiftReminderTime: hhmm,
    /** Safety net: the shift closes by itself at this time (app time zone). */
    shiftAutoStopTime: hhmm,
    /** Kilometres walked: points less accurate than this are ignored (D92). */
    maxAccuracyMeters: z.number().int().min(10).max(500),
    /** Kilometres walked: moves shorter than this are GPS jitter and not added up. */
    minMovementMeters: z.number().int().min(0).max(200),
    /** Kilometres walked: a jump faster than this between two points is a bad fix, ignored. */
    maxSpeedKmh: z.number().int().min(20).max(300),
    /** Live map: a point older than this is shown as stale, never as current. */
    liveStaleAfterSeconds: z.number().int().min(60).max(7200),
    /** Live map refresh (polling with ETag). */
    livePollSeconds: z.number().int().min(30).max(60),
    /** A stretch of an open shift without any point longer than this is a coverage gap. */
    gapThresholdMinutes: z.number().int().min(3).max(120),
    /** Idle period: staying within this radius for at least `idleMinMinutes`. */
    idleRadiusMeters: z.number().int().min(20).max(500),
    idleMinMinutes: z.number().int().min(5).max(240),
    /** Raw points older than this are deleted (the only data with a retention window, D91). */
    retentionDays: z.number().int().min(7).max(730),
});
export const DEFAULT_TRACKING_SETTINGS = {
    sampleMinIntervalSeconds: 60,
    sampleMaxIntervalSeconds: 120,
    sampleDistanceMeters: 100,
    accuracy: 'BALANCED',
    uploadIntervalSeconds: 300,
    uploadBatchSize: 30,
    shiftReminderTime: '19:00',
    shiftAutoStopTime: '21:00',
    maxAccuracyMeters: 50,
    minMovementMeters: 20,
    maxSpeedKmh: 120,
    liveStaleAfterSeconds: 600,
    livePollSeconds: 45,
    gapThresholdMinutes: 6,
    idleRadiusMeters: 75,
    idleMinMinutes: 15,
    retentionDays: 90,
};
export const updateTrackingSettingsRequestSchema = trackingSettingsSchema
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'At least one setting must be provided');
/** Merges stored settings over the defaults, so a newly added setting always has a value. */
export function resolveTrackingSettings(stored) {
    const partial = trackingSettingsSchema.partial().safeParse(stored ?? {});
    const merged = { ...DEFAULT_TRACKING_SETTINGS, ...(partial.success ? partial.data : {}) };
    if (merged.sampleMaxIntervalSeconds < merged.sampleMinIntervalSeconds)
        merged.sampleMaxIntervalSeconds = merged.sampleMinIntervalSeconds;
    return merged;
}
/** Hard ceiling on one upload: the server refuses (never truncates) more. */
export const MAX_POINTS_PER_BATCH = 200;
export const MAX_EVENTS_PER_BATCH = 100;
// ---------------------------------------------------------------------------
// Shifts, points, device events
// ---------------------------------------------------------------------------
/** What the agent granted: "always" is needed for background tracking. */
export const LOCATION_PERMISSION_LEVELS = ['ALWAYS', 'WHILE_IN_USE', 'DENIED', 'UNKNOWN'];
export const SHIFT_END_REASONS = ['AGENT', 'AUTO_STOP', 'SERVER_AUTO_CLOSE', 'CONSENT_WITHDRAWN'];
const isoTime = z.iso.datetime({ offset: true });
export const startShiftRequestSchema = z.object({
    /** Generated on the phone: the idempotency key (a queued start sent twice opens one shift). */
    clientShiftId: z.uuid(),
    at: isoTime,
    permissionLevel: z.enum(LOCATION_PERMISSION_LEVELS),
});
export const endShiftRequestSchema = z.object({ at: isoTime, reason: z.enum(SHIFT_END_REASONS).default('AGENT') });
export const locationPointSchema = z.object({
    /** Generated on the phone: the idempotency key of the point. */
    clientPointId: z.uuid(),
    clientShiftId: z.uuid(),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    accuracyMeters: z.number().min(0).max(100000).nullable(),
    /** When the phone took the fix. The server adds its own receive time. */
    deviceTime: isoTime,
    /** 0..1, null when unknown. */
    batteryLevel: z.number().min(0).max(1).nullable(),
    isMocked: z.boolean().nullable(),
    /** The visit the agent had open (client submission id), if any. */
    activeVisitId: z.uuid().nullable(),
});
/** What the phone reports about its own tracking, so gaps can be explained (D89). */
export const TRACKING_EVENT_TYPES = [
    /** The JS app started from scratch while a shift was open (it had been closed or killed). */
    'APP_COLD_START',
    'APP_BACKGROUND',
    'APP_FOREGROUND',
    'PERMISSION_CHANGED',
    'LOCATION_SERVICES_OFF',
    'LOCATION_SERVICES_ON',
    'BATTERY_SAVER_ON',
    'BATTERY_SAVER_OFF',
    'TRACKING_STARTED',
    'TRACKING_STOPPED',
    'TRACKING_ERROR',
];
export const trackingEventSchema = z.object({
    clientEventId: z.uuid(),
    clientShiftId: z.uuid().nullable(),
    type: z.enum(TRACKING_EVENT_TYPES),
    deviceTime: isoTime,
    permissionLevel: z.enum(LOCATION_PERMISSION_LEVELS).nullable(),
    detail: z.string().max(300).nullable(),
});
export const uploadPointsRequestSchema = z.object({
    points: z.array(locationPointSchema).max(MAX_POINTS_PER_BATCH),
    events: z.array(trackingEventSchema).max(MAX_EVENTS_PER_BATCH).default([]),
});
export const uploadPointsResponseSchema = z.object({
    /** New points stored. Points seen before (same clientPointId) are acknowledged, not stored twice. */
    accepted: z.number().int(),
    duplicates: z.number().int(),
    /** Points for a shift the server does not know or that is closed far before them: dropped and counted. */
    rejected: z.number().int(),
    events: z.number().int(),
});
export const shiftDtoSchema = z.object({
    id: z.string(),
    clientShiftId: z.string(),
    startedAt: z.string(),
    endedAt: z.string().nullable(),
    endReason: z.enum(SHIFT_END_REASONS).nullable(),
    permissionLevel: z.enum(LOCATION_PERMISSION_LEVELS),
    lastPointAt: z.string().nullable(),
});
/** Everything the phone needs to run tracking: settings, consent and the open shift. */
export const mobileTrackingStateSchema = z.object({
    settings: trackingSettingsSchema,
    consent: consentStatusSchema,
    openShift: shiftDtoSchema.nullable(),
    /** The agent's own figures for today. */
    today: z.object({ date: z.string(), walkedMeters: z.number().nullable(), coveragePercent: z.number().nullable() }),
});
/**
 * The phone gets a fix every `sampleMinIntervalSeconds`. It keeps it when the
 * agent moved at least `sampleDistanceMeters` since the last kept point, or
 * when `sampleMaxIntervalSeconds` passed: a point per 60-120 s or per 100 m,
 * whichever comes first (defaults).
 */
export function shouldKeepFix(last, next, settings) {
    if (!last)
        return true;
    const elapsed = (next.time - last.time) / 1000;
    if (elapsed < 0)
        return false;
    if (elapsed >= settings.sampleMaxIntervalSeconds)
        return true;
    return elapsed >= settings.sampleMinIntervalSeconds * 0.8 && haversineMeters(last, next) >= settings.sampleDistanceMeters;
}
/** "HH:mm" today, in minutes since midnight. */
export const minutesOfDay = (hhmmText) => {
    const [h, m] = hhmmText.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
};
/**
 * Sum of the distances between consecutive usable points. A point is unusable
 * when its accuracy is worse than `maxAccuracyMeters` (or unknown), or when
 * reaching it would need an impossible speed. Moves shorter than
 * `minMovementMeters` are not added, but do not move the anchor either, so
 * slow real walking still adds up once it exceeds the threshold, while GPS
 * jitter around one spot never does.
 */
export function computeWalkedMeters(points, settings) {
    const usable = points
        .filter((p) => p.accuracyMeters !== null && p.accuracyMeters <= settings.maxAccuracyMeters)
        .sort((a, b) => a.time - b.time);
    let total = 0;
    let anchor = null;
    const maxSpeed = settings.maxSpeedKmh / 3.6;
    for (const point of usable) {
        if (!anchor) {
            anchor = point;
            continue;
        }
        const meters = haversineMeters(anchor, point);
        const seconds = Math.max(1, (point.time - anchor.time) / 1000);
        if (meters / seconds > maxSpeed)
            continue; // a bad fix: skip it, keep the anchor
        if (meters < settings.minMovementMeters)
            continue; // jitter: not a move yet
        total += meters;
        anchor = point;
    }
    return Math.round(total);
}
// ---------------------------------------------------------------------------
// Coverage and gaps (D89)
// ---------------------------------------------------------------------------
export const GAP_REASONS = ['APP_CLOSED', 'PERMISSION_REVOKED', 'LOCATION_OFF', 'BATTERY_SAVER', 'NO_SIGNAL'];
/**
 * Stretches of an open shift with no point for longer than the threshold, and
 * why: the most specific cause the phone reported wins. A cold start at the end
 * of the gap means the app had been closed or killed; a permission change to
 * anything but "always" means it was revoked; location services or battery
 * saver reported on during it; otherwise no fix was available (indoors,
 * underground, GPS off by the system): "no signal".
 */
export function findCoverageGaps(shifts, pointTimes, events, thresholdMinutes, now) {
    const gaps = [];
    const sortedPoints = [...pointTimes].sort((a, b) => a - b);
    const sortedEvents = [...events].sort((a, b) => a.time - b.time);
    const threshold = thresholdMinutes * 60000;
    for (const shift of shifts) {
        const end = Math.min(shift.endedAt ?? now, now);
        if (end <= shift.startedAt)
            continue;
        const inside = sortedPoints.filter((t) => t >= shift.startedAt && t <= end);
        const marks = [shift.startedAt, ...inside, end];
        for (let i = 1; i < marks.length; i++) {
            const from = marks[i - 1];
            const to = marks[i];
            if (to - from <= threshold)
                continue;
            gaps.push({ from, to, minutes: Math.round((to - from) / 60000), reason: gapReason(sortedEvents, from, to) });
        }
    }
    return gaps;
}
function gapReason(events, from, to) {
    // An event a little after the gap still explains it (the phone reports when it wakes up).
    const slack = 5 * 60000;
    const during = events.filter((e) => e.time >= from && e.time <= to + slack);
    const before = [...events].reverse().find((e) => e.time < from);
    const has = (type) => during.some((e) => e.type === type);
    const revoked = (e) => e?.type === 'PERMISSION_CHANGED' && e.permissionLevel !== null && e.permissionLevel !== 'ALWAYS';
    if (during.some(revoked) || revoked(before))
        return 'PERMISSION_REVOKED';
    if (has('LOCATION_SERVICES_OFF') || before?.type === 'LOCATION_SERVICES_OFF')
        return 'LOCATION_OFF';
    if (has('BATTERY_SAVER_ON') || before?.type === 'BATTERY_SAVER_ON')
        return 'BATTERY_SAVER';
    if (has('APP_COLD_START'))
        return 'APP_CLOSED';
    return 'NO_SIGNAL';
}
/** Share of the shift time that is not inside a gap; null when there was no shift time. */
export function coveragePercent(shifts, gaps, now) {
    const total = shifts.reduce((sum, s) => sum + Math.max(0, Math.min(s.endedAt ?? now, now) - s.startedAt), 0);
    if (total <= 0)
        return null;
    const missing = gaps.reduce((sum, g) => sum + (g.to - g.from), 0);
    return Math.max(0, Math.min(100, Math.round(((total - missing) / total) * 1000) / 10));
}
/** Periods spent within `radius` metres of one spot for at least `minMinutes` (accurate points only). */
export function findIdlePeriods(points, radius, minMinutes, maxAccuracy) {
    const usable = points.filter((p) => p.accuracyMeters !== null && p.accuracyMeters <= maxAccuracy).sort((a, b) => a.time - b.time);
    const periods = [];
    let start = 0;
    while (start < usable.length) {
        let end = start;
        while (end + 1 < usable.length && haversineMeters(usable[start], usable[end + 1]) <= radius)
            end++;
        const minutes = (usable[end].time - usable[start].time) / 60000;
        if (minutes >= minMinutes) {
            const slice = usable.slice(start, end + 1);
            periods.push({
                from: usable[start].time,
                to: usable[end].time,
                minutes: Math.round(minutes),
                center: {
                    latitude: slice.reduce((s, p) => s + p.latitude, 0) / slice.length,
                    longitude: slice.reduce((s, p) => s + p.longitude, 0) / slice.length,
                },
            });
            start = end + 1;
        }
        else {
            start++;
        }
    }
    return periods;
}
// ---------------------------------------------------------------------------
// Live map (D89)
// ---------------------------------------------------------------------------
export const LIVE_STATES = ['LIVE', 'STALE', 'SHIFT_ENDED', 'CONSENT_MISSING', 'PERMISSION_DENIED', 'NO_POINT_YET'];
/** Never shows a stale point as current: the state comes from the age of the last point, not from the shift alone. */
export function liveState(input) {
    if (!input.consented)
        return 'CONSENT_MISSING';
    if (!input.shiftOpen)
        return 'SHIFT_ENDED';
    if (input.permissionLevel === 'DENIED')
        return 'PERMISSION_DENIED';
    if (input.lastPointAt === null)
        return 'NO_POINT_YET';
    return input.now - input.lastPointAt <= input.staleAfterSeconds * 1000 ? 'LIVE' : 'STALE';
}
const person = z.object({ id: z.string(), fullName: z.string() });
export const liveAgentSchema = z.object({
    agent: person,
    state: z.enum(LIVE_STATES),
    shiftStartedAt: z.string().nullable(),
    shiftEndedAt: z.string().nullable(),
    permissionLevel: z.enum(LOCATION_PERMISSION_LEVELS).nullable(),
    lastPoint: z
        .object({
        latitude: z.number(),
        longitude: z.number(),
        accuracyMeters: z.number().nullable(),
        deviceTime: z.string(),
        batteryLevel: z.number().nullable(),
        isMocked: z.boolean().nullable(),
    })
        .nullable(),
    /** Seconds since the last point (server clock); null when there is none. */
    secondsSinceLastPoint: z.number().nullable(),
});
export const liveResponseSchema = z.object({
    generatedAt: z.string(),
    pollSeconds: z.number().int(),
    staleAfterSeconds: z.number().int(),
    agents: z.array(liveAgentSchema),
});
// ---------------------------------------------------------------------------
// Daily route report and summaries (D89, D92)
// ---------------------------------------------------------------------------
export const routeReportQuerySchema = z.object({ agentId: z.uuid(), date: z.iso.date() });
export const routeReportSchema = z.object({
    agent: person,
    date: z.string(),
    shifts: z.array(shiftDtoSchema),
    /** The raw track, oldest first; empty once past the retention window. */
    points: z.array(z.object({ latitude: z.number(), longitude: z.number(), accuracyMeters: z.number().nullable(), deviceTime: z.string() })),
    pointsPurged: z.boolean(),
    visits: z.array(z.object({
        visitId: z.string(),
        branchName: z.string().nullable(),
        startedAt: z.string(),
        endedAt: z.string(),
        minutes: z.number(),
        latitude: z.number().nullable(),
        longitude: z.number().nullable(),
    })),
    idle: z.array(z.object({ from: z.string(), to: z.string(), minutes: z.number(), latitude: z.number(), longitude: z.number() })),
    gaps: z.array(z.object({ from: z.string(), to: z.string(), minutes: z.number(), reason: z.enum(GAP_REASONS) })),
    /** Timeline: shifts, visits, idle periods and gaps in time order. */
    timeline: z.array(z.object({
        at: z.string(),
        until: z.string().nullable(),
        kind: z.enum(['SHIFT_START', 'SHIFT_END', 'VISIT', 'IDLE', 'GAP']),
        label: z.string(),
    })),
    summary: z.object({
        shiftMinutes: z.number(),
        visitCount: z.number().int(),
        walkedMeters: z.number().nullable(),
        coveragePercent: z.number().nullable(),
        pointCount: z.number().int(),
    }),
});
export const dailySummaryQuerySchema = z.object({ agentId: z.uuid().optional() }).and(dateRangeQuerySchema);
export const dailySummaryRowSchema = z.object({
    date: z.string(),
    agent: person,
    shiftMinutes: z.number().nullable(),
    visitCount: z.number().int(),
    walkedMeters: z.number().nullable(),
    /** Shown next to every distance: a 40% day is not comparable to a full one. */
    coveragePercent: z.number().nullable(),
    totalGapMinutes: z.number().nullable(),
    straightLineMeters: z.number().nullable(),
    unknownDistancePairs: z.number().int(),
});
export const dailySummaryResponseSchema = z.object({
    rows: z.array(dailySummaryRowSchema),
    /** Per agent over the range; walked metres summed over days with tracking, with the average coverage. */
    agents: z.array(z.object({
        agent: person,
        days: z.number().int(),
        trackedDays: z.number().int(),
        walkedMeters: z.number(),
        avgCoveragePercent: z.number().nullable(),
        visitCount: z.number().int(),
        shiftMinutes: z.number(),
    })),
});
/** A low-coverage day is labelled, never compared silently with a full one (D92). */
export const LOW_COVERAGE_PERCENT = 80;
//# sourceMappingURL=tracking.js.map